import { css, nothing } from 'lit'
import { html, unsafeStatic } from 'lit/static-html.js'
import HHQuizElement from '../element.js'
import { validateEmail } from '../../lib/utils'
import trySignupCustomer from '../store/utility-signup.js'
import { updateRemoteStorage } from '../store/utility-remote-storage.js'

/**
 * Question view
 * @extends
 */
class HHQuizViewIntro extends HHQuizElement {
  static properties = {
    emailError: { type: Boolean },
  }

  /**
   * Adds 'Enter' key listener
   */
  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('keydown', this.handleKeydown, false)
  }

  /**
   * - Implements customer signup on leave
   * - Removes 'Enter' key listener
   */
  disconnectedCallback() {
    window.removeEventListener('keydown', this.handleKeydown, false)
    trySignupCustomer()
    super.disconnectedCallback()
  }

  /**
   * Navigates to the next route
   * @returns {void}
   */
  handleKeydown = (ev) => {
    if (ev.key !== 'Enter' || !this.store.customerIsCompleted) return
    this.router.next()
  }

  updateCustomer = (data) => {
    this.timeout = this.timeout ?? {}
    Object.keys(data).forEach((key) => {
      clearTimeout(this.timeout[key])
      this.timeout[key] = setTimeout(() => {
        this.store.updateCustomer({
          [key]: data[key],
        })
      }, 300)
    })
  }

  updateCustomerEmail = (email) => {
    //If email is valid, set emailError to false
    if (validateEmail(email)) {
      this.emailError = false
      //If previously email was filled and now is invalid, set emailError to true
    } else if (validateEmail(this.store.customer.email)) {
      this.emailError = true
    }

    //Update customer email (even if it's not valid)
    this.updateCustomer({ email })
  }

  /**
   * Renders the current route
   * @returns {TemplateResult}
   */
  render() {
    return html`
      <div class="hh-quiz-view-intro">
        <div class="hh-quiz-view-intro__image">
          <div>
            ${this.store.quiz.introImage
              ? html`<img
                  src="${this.store.quiz.introImage.src}"
                  alt="${this.store.quiz.introImage.alt}"
                />`
              : nothing}
          </div>
        </div>
        <div class="hh-quiz-view-intro__content">
          <div>
            <div
              class="hh-quiz-view-intro__content__text focus-none focus-first"
              tabindex="0"
            >
              <h1 class="h2">
                ${unsafeStatic(this.store.quiz.texts.intro.title)}
              </h1>
              <p class="body">
                ${unsafeStatic(this.store.quiz.texts.intro.message)}
              </p>
            </div>
            <div class="hh-quiz-view-intro__content__form">
              <div class="hh-quiz-input">
                <input
                  type="text"
                  id="customer-name"
                  required
                  placeholder="${HH_QUIZ.t.customer.enter_name}"
                  class="body"
                  value="${this.store.customer.name}"
                  @keyup="${(ev) =>
                    this.updateCustomer({ name: ev.target.value })}"
                  @keydown="${(ev) => ev.stopPropagation()}"
                />
                <label for="customer-name"
                  >${HH_QUIZ.t.customer.enter_name}</label
                >
              </div>
              <div class="hh-quiz-input">
                <input
                  type="email"
                  id="customer-email"
                  placeholder="${HH_QUIZ.t.customer.enter_email}"
                  required
                  class="body"
                  value="${this.store.customer.email}"
                  @keyup="${(ev) => this.updateCustomerEmail(ev.target.value)}"
                  @keydown="${(ev) => ev.stopPropagation()}"
                  @change="${(ev) =>
                    (this.emailError = !validateEmail(ev.target.value))}"
                />
                <label for="customer-email"
                  >${HH_QUIZ.t.customer.enter_email}</label
                >
              </div>
              ${this.emailError
                ? html`<span class="error"
                    >${HH_QUIZ.t.customer.enter_valid_email}</span
                  >`
                : nothing}
              <fieldset class="body" @keydown="${(ev) => ev.stopPropagation()}">
                <input
                  type="checkbox"
                  id="quiz_newsletter_optin"
                  ?checked="${this.store.customer.signupOptIn}"
                  value="${HH_QUIZ.t.customer.subscribe_optin}"
                  @keydown="${(ev) => {
                    if (ev.key !== 'Enter') return
                    ev.target.checked = !ev.target.checked
                    ev.stopPropagation()
                  }}"
                  @change="${(ev) =>
                    this.store.updateCustomer({
                      signupOptIn: ev.target.checked,
                    })}"
                />
                <label
                  class="body"
                  for="quiz_newsletter_optin"
                  @keydown="${(ev) => ev.stopPropagation()}"
                  >${HH_QUIZ.t.customer.subscribe_optin}</label
                >
              </fieldset>
            </div>
            <hh-quiz-router-link
              @click="${() => updateRemoteStorage()}"
              .disabled="${!this.store.customerIsCompleted}"
              component="hh-button"
              to="${this.router.nextPath}"
            >
              ${HH_QUIZ.t.actions.continue}
            </hh-quiz-router-link>
          </div>
        </div>
      </div>
    `
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-view-intro {
        display: grid;
        grid-template-columns: 1fr 1fr;
        flex-grow: 1;
        min-height: calc(100vh - 212px);
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-view-intro {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: calc(100vh - 300px);
        }
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-view-intro {
          align-items: flex-start;
        }
      }

      .hh-quiz-view-intro__image {
        padding: 60px;
        display: flex;
        align-items: stretch;
      }

      @media screen and (max-width: ${HHQuizElement.mq.xl}px) {
        .hh-quiz-view-intro__image {
          padding: 60px 30px 60px 60px;
        }
      }

      @media screen and (max-width: ${HHQuizElement.mq.lg}px) {
        .hh-quiz-view-intro__image {
          padding: 60px 0 60px 30px;
        }
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-view-intro__image {
          display: none;
        }
      }

      .hh-quiz-view-intro__image > div {
        background-color: var(--hh-color-grey-mineral);
        flex-grow: 1;
        position: relative;
        overflow: hidden;
      }

      .hh-quiz-view-intro__image img {
        display: block;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }

      .hh-quiz-view-intro__content {
        display: flex;
        padding: 60px;
        align-items: center;
        justify-content: center;
      }

      @media screen and (max-width: ${HHQuizElement.mq.md}px) {
        .hh-quiz-view-intro__content {
          padding: 50px;
        }
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-view-intro__content {
          padding: 30px;
        }
      }

      @media screen and (max-width: ${HHQuizElement.mq.xs}px) {
        .hh-quiz-view-intro__content {
          padding: 30px 20px 30px 20px;
        }
      }

      .hh-quiz-view-intro__content > div {
        flex-grow: 1;
        max-width: max(33vw, 480px);
      }

      input[type='text'],
      input[type='email'] {
        display: block;
        border: none;
        background: none;
        height: 48px;
        border-bottom: 1px solid var(--hh-color-aged-black);
        width: 100%;
        margin-bottom: 20px;
      }

      .hh-quiz-view-intro__content__text {
        margin-bottom: 48px;
      }

      .hh-quiz-view-intro__content__form {
        margin-bottom: 28px;
      }

      fieldset,
      label,
      input[type='checkbox'] {
        border: 0;
        padding: 0;
        margin: 0;
        min-width: 0;
        display: block;
        position: relative;
      }
      fieldset {
        display: flex;
        align-items: center;
        justify-content: start;
      }
      input[type='checkbox'] {
        z-index: -1;
        width: 14px;
        height: 14px;
        position: absolute;
        top: 2px;
        left: 1px;
        opacity: 0;
      }
      input[type='checkbox'] + label:before {
        content: '';
        display: block;
        width: 16px;
        height: 16px;
        background: var(--hh-color-star-white);
        position: absolute;
        top: 0.1em;
        left: 0;
        z-index: 10;
        border: 1px solid var(--hh-color-aged-black);
        box-sizing: border-box;
        cursor: pointer;
      }
      input[type='checkbox']:focus-visible + label:before {
        border-color: var(--hh-color-focus);
        outline: 1px solid var(--hh-color-focus);
      }
      input[type='checkbox'] + label:after {
        content: '';
        display: block;
        width: 10px;
        height: 10px;
        background: var(--hh-color-honey-two);
        position: absolute;
        top: calc(0.1em + 3px);
        left: 3px;
        z-index: 10;
        cursor: pointer;
        opacity: 0;
      }
      input[type='checkbox']:checked + label:after {
        opacity: 1;
      }
      @media screen and (min-width: ${HHQuizElement.mq['2xl']}px) {
        input[type='checkbox'] + label:before {
          top: 0.2em;
        }
        input[type='checkbox'] + label:after {
          top: calc(0.2em + 3px);
        }
      }
      input[type='checkbox'] + label {
        padding-left: 23px;
        cursor: pointer;
      }

      .error {
        display: block;
        color: var(--hh-color-danger);
        margin-bottom: 20px;
      }

      .hh-quiz-input {
        position: relative;
      }

      .hh-quiz-input input {
        position: relative;
        z-index: 1;
        padding-top: 13px;
        height: 35px;
      }

      .hh-quiz-input input::placeholder {
        color: transparent;
      }

      .hh-quiz-input input + label {
        position: absolute;
        left: 2px;
        top: 13px;
        color: var(--hh-color-unrefined-silver);
        transition: var(--hh-duration-default, 300ms);
        z-index: 0;
      }

      .hh-quiz-input input:focus + label,
      .hh-quiz-input input:not(:placeholder-shown) + label {
        top: 0;
        left: 2px;
        color: var(--hh-color-unrefined-silver);
        font: var(--hh-body-caption);
        letter-spacing: -0.36px;
      }
    `,
  ]
}

customElements.define('hh-quiz-view-intro', HHQuizViewIntro)
